import { useEffect, useId, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../api';
import { useApp } from '../state';
import { AuthHead } from '../components/AuthHead';

// Connexion à la console EDI : chemin NORMAL = SSO Orgazone (bouton → /v1/auth/oidc/login qui sonde l'IdP,
// repli local si down) ; chemin de SECOURS = connexion LOCALE (bris de glace §24.9quinquies h), un compte
// local à mot de passe posé par l'env. ⚠️ Un compte fédéré (passwordHash=null) ne peut PAS se connecter ici.
// Rien n'est simulé (§23.0) : le formulaire POSTe réellement /v1/auth/login et affiche le verdict serveur.
export function Login() {
  /** Le préfixe des identifiants qui relient chaque libellé à son champ. */
  const fieldId = useId();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const { authReady, authMode, authError, sessionUser, refreshSession } = useApp();
  const degraded = sp.get('degraded') === '1'; // posé par le repli OIDC quand l'IdP est injoignable
  const [mail, setMail] = useState('');
  const [pass, setPass] = useState('');
  const [remember, setRemember] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  // Déjà connecté (ou mode ouvert) → l'app, pas le login.
  // ⚠️ ATTENDRE `authReady` : sans cette garde, l'état INITIAL (armed=false, session pas encore résolue)
  // déclenchait un aller-retour /login → / → /login à chaque chargement, qui PERDAIT la query string. Le
  // repli `?degraded=1` (IdP injoignable) disparaissait donc, le bouton SSO se réaffichait, l'utilisateur
  // recliquait, l'IdP était toujours down… → boucle infinie login local ↔ SSO, sans jamais dire pourquoi.
  useEffect(() => {
    if (!authReady) return;
    if (sessionUser || !authMode.armed) nav('/', { replace: true });
  }, [authReady, sessionUser, authMode.armed, nav]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr(''); setBusy(true);
    try {
      const r = await api.login(mail.trim(), pass, remember);
      if (r.status === '2fa_required') { setErr('2FA requise — non gérée par cette page de secours (passez par le SSO).'); return; }
      await refreshSession();
      nav('/', { replace: true });
    } catch (e) {
      setErr((e as Error).message || 'Identifiants refusés.');
    } finally {
      setBusy(false);
    }
  };

  const label = { fontSize: 'var(--t-body)', fontWeight: 600, color: '#4a5158', display: 'block', marginBottom: 6 } as const;
  const input = { width: '100%', height: 44, padding: '0 13px', borderRadius: 'var(--r-sm)', border: '1px solid #e2e6ea', background: 'var(--paper)', color: '#131618', fontSize: 'var(--t-control)' } as const;
  const showSso = authMode.oidc && !degraded; // SSO proposé si l'IdP est configuré et qu'on n'est pas en repli

  return (
    <div style={{ minHeight: '100%', display: 'grid', placeItems: 'center', background: 'linear-gradient(180deg, #effdf8, #f4f5f7)', padding: 20 }}>
      <form onSubmit={submit} style={{ width: 400, maxWidth: '100%', background: 'var(--paper)', borderRadius: 'var(--r-lg)', padding: 30, boxShadow: '0 20px 50px -20px rgba(15,20,25,.3)' }}>
        <AuthHead title={showSso ? 'Connexion' : 'Connexion locale'} />

        {/* Chemin normal : SSO Orgazone (redirige vers l'IdP). */}
        {showSso && (
          <>
            <a href="/v1/auth/oidc/login" style={{ display: 'block', textAlign: 'center', width: '100%', height: 48, lineHeight: '48px', borderRadius: 'var(--r-sm)', background: '#131618', color: '#fff', fontSize: 'var(--t-h4)', fontWeight: 600, textDecoration: 'none', marginBottom: 16 }}>
              Se connecter avec Orgazone (SSO)
            </a>
            <div style={{ textAlign: 'center', fontSize: 'var(--t-dense)', color: '#9aa1a8', margin: '4px 0 14px' }}>ou, en secours</div>
          </>
        )}

        {/* Service d'authentification injoignable : on le DIT (§23.0). Sans ce signal l'écran semblait
            simplement « refuser » la connexion, alors que c'est le serveur qui ne répond pas. */}
        {authError && (
          <div style={{ background: '#fdeaea', border: '1px solid #f3c3c3', color: '#b23', borderRadius: 'var(--r-sm)', padding: '11px 13px', fontSize: 'var(--t-body)', lineHeight: 1.45, marginBottom: 18 }}>
            <strong>Service d'authentification injoignable.</strong> La connexion ne peut pas aboutir pour l'instant : {authError}
          </div>
        )}

        {/* Bandeau mode dégradé : affiché SEULEMENT au repli (IdP down) — pas un décor permanent. */}
        {degraded && (
          <div style={{ background: '#fff8ec', border: '1px solid #f3dcae', color: '#8a6d3b', borderRadius: 'var(--r-sm)', padding: '11px 13px', fontSize: 'var(--t-body)', lineHeight: 1.45, marginBottom: 18 }}>
            <strong>Authentification Orgazone indisponible.</strong> Utilisez la <strong>connexion locale de secours</strong> (bris de glace). Réessayez le SSO plus tard.
          </div>
        )}

        {err && <div style={{ background: '#fdeaea', color: '#b23', borderRadius: 'var(--r-sm)', padding: '10px 12px', fontSize: 'var(--t-lead)', marginBottom: 12, lineHeight: 1.45 }}>{err}</div>}

        <div style={{ marginBottom: 14 }}>
          <label htmlFor={`${fieldId}-0`} style={label}>Adresse e-mail</label>
          <input id={`${fieldId}-0`} style={input} type="email" value={mail} onChange={(e) => { setMail(e.target.value); setErr(''); }} autoComplete="username" required autoFocus />
        </div>
        <div style={{ marginBottom: 12 }}>
          <label htmlFor={`${fieldId}-1`} style={label}>Mot de passe</label>
          <input id={`${fieldId}-1`} style={input} type="password" value={pass} onChange={(e) => { setPass(e.target.value); setErr(''); }} autoComplete="current-password" required />
        </div>

        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 18 }}>
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 'var(--t-lead)', color: '#4a5158', cursor: 'pointer' }}>
            <input type="checkbox" checked={remember} onChange={() => setRemember((v) => !v)} /> Se souvenir de moi
          </label>
        </div>

        {/* ⚠️ Le bicolore d'avant (turquoise si le SSO est proposé, noir sinon) disparaît : il codait
            une nuance que rien n'expliquait à l'écran, et il posait deux hex en dur sur la seule page
            où la charte n'était pas encore appliquée. Une action principale a UNE apparence. */}
        <button type="submit" disabled={busy} className="btn-cta" style={{ width: '100%', height: 48, borderRadius: 'var(--r-sm)', fontSize: 'var(--t-h4)', cursor: busy ? 'wait' : undefined }}>
          {busy ? 'Connexion…' : 'Connexion locale'}
        </button>

        {/* Le parcours de réinitialisation EXISTAIT côté serveur sans qu'aucun lien n'y mène : perdre le
            mot de passe de secours voulait dire re-provisionner le compte à la main. */}
        <button type="button" onClick={() => nav('/reset')} style={{ display: 'block', width: '100%', marginTop: 14, background: 'none', border: 'none', fontSize: 'var(--t-dense)', color: '#16a585', fontWeight: 600, cursor: 'pointer' }}>
          Mot de passe oublié ?
        </button>

        <p style={{ textAlign: 'center', fontSize: 'var(--t-dense)', color: '#6c747c', marginTop: 14, lineHeight: 1.5 }}>
          Accès réservé au personnel Orgazone. Le compte de secours ne sert qu'au dépannage.
        </p>
      </form>
    </div>
  );
}
